/**
 * Storelancer shared UI primitives (server-safe). Interactive pieces live in ./client.tsx.
 * Conventions: zinc neutrals, brand-600 primary, rounded-lg, 44px min touch targets on primary actions.
 */
import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";
import { StatusText, UiNav, UiText } from "./i18n";

export function cx(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}

type Variant = "primary" | "secondary" | "danger" | "ghost" | "outline";
const BTN: Record<Variant, string> = {
  primary: "bg-brand-600 text-white hover:bg-brand-700 shadow-sm",
  secondary: "bg-white text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 shadow-sm",
  outline: "bg-transparent text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100",
  danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
  ghost: "bg-transparent text-zinc-700 hover:bg-zinc-100",
};
const SIZE = { sm: "h-8 px-3 text-sm", md: "h-10 px-4 text-sm", lg: "h-12 px-6 text-base" };

export function buttonClass(variant: Variant = "primary", size: keyof typeof SIZE = "md", extra?: string) {
  return cx("inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap", BTN[variant], SIZE[size], extra);
}

export function Button({ variant = "primary", size = "md", className, ...p }: ComponentProps<"button"> & { variant?: Variant; size?: keyof typeof SIZE }) {
  return <button {...p} className={buttonClass(variant, size, className)} />;
}

export function ButtonLink({ variant = "primary", size = "md", className, ...p }: ComponentProps<typeof Link> & { variant?: Variant; size?: keyof typeof SIZE }) {
  return <Link {...p} className={buttonClass(variant, size, className)} />;
}

export const inputClass =
  "block w-full rounded-lg border-0 bg-white px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 disabled:bg-zinc-50 disabled:text-zinc-500 aria-[invalid=true]:ring-red-500";

export function Input({ className, ...p }: ComponentProps<"input">) {
  return <input {...p} className={cx(inputClass, "h-10", className)} />;
}
export function Textarea({ className, ...p }: ComponentProps<"textarea">) {
  return <textarea {...p} className={cx(inputClass, "min-h-24", className)} />;
}
export function Select({ className, children, ...p }: ComponentProps<"select">) {
  return (
    <select {...p} className={cx(inputClass, "h-10 pe-8", className)}>
      {children}
    </select>
  );
}

export function Label({ className, ...p }: ComponentProps<"label">) {
  return <label {...p} className={cx("block text-sm font-medium text-zinc-800", className)} />;
}

/** Labelled field with hint and error. Pass `name` to wire the input id/aria automatically via children. */
export function Field({ label, htmlFor, hint, error, children, className, optional }: { label: ReactNode; htmlFor?: string; hint?: ReactNode; error?: string; children: ReactNode; className?: string; optional?: boolean }) {
  return (
    <div className={cx("space-y-1.5", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {optional && <span className="ms-1 font-normal text-zinc-400"><UiText k="ui.optional" /></span>}
      </Label>
      {children}
      {error ? (
        <p className="text-sm text-red-600" id={htmlFor ? `${htmlFor}-error` : undefined} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-zinc-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Checkbox({ label, hint, className, ...p }: ComponentProps<"input"> & { label: ReactNode; hint?: ReactNode }) {
  return (
    <label className={cx("flex items-start gap-3 text-sm", className)}>
      <input type="checkbox" {...p} className="mt-0.5 size-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-600" />
      <span>
        <span className="font-medium text-zinc-800">{label}</span>
        {hint && <span className="block text-zinc-500">{hint}</span>}
      </span>
    </label>
  );
}

export function Card({ className, children, ...p }: ComponentProps<"div">) {
  return (
    <div {...p} className={cx("rounded-xl bg-white shadow-sm ring-1 ring-zinc-950/5", className)}>
      {children}
    </div>
  );
}

export function CardHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
      <div>
        <h2 className="text-base font-semibold text-zinc-900">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-zinc-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, ...p }: ComponentProps<"div">) {
  return <div {...p} className={cx("px-5 py-4", className)} />;
}

export function PageHeader({ title, description, actions, breadcrumbs, badge }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; breadcrumbs?: { label: string; href?: string }[]; badge?: ReactNode }) {
  return (
    <div className="mb-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <UiNav labelKey="ui.breadcrumb" className="mb-2 text-sm text-zinc-500">
          <ol className="flex flex-wrap items-center gap-1">
            {breadcrumbs.map((b, i) => (
              <li key={i} className="flex items-center gap-1">
                {i > 0 && <span aria-hidden>/</span>}
                {b.href ? (
                  <Link href={b.href} className="hover:text-zinc-900">
                    {b.label}
                  </Link>
                ) : (
                  <span aria-current="page">{b.label}</span>
                )}
              </li>
            ))}
          </ol>
        </UiNav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">{title}</h1>
            {badge}
          </div>
          {description && <p className="mt-1 max-w-3xl text-sm text-zinc-600">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

type Tone = "neutral" | "green" | "yellow" | "red" | "blue" | "purple" | "orange";
const TONES: Record<Tone, string> = {
  neutral: "bg-zinc-100 text-zinc-700 ring-zinc-500/20",
  green: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  yellow: "bg-amber-50 text-amber-800 ring-amber-600/20",
  red: "bg-red-50 text-red-700 ring-red-600/20",
  blue: "bg-sky-50 text-sky-700 ring-sky-600/20",
  purple: "bg-violet-50 text-violet-700 ring-violet-600/20",
  orange: "bg-orange-50 text-orange-700 ring-orange-600/20",
};
export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset", TONES[tone], className)}>{children}</span>;
}

const STATUS_TONES: Record<string, Tone> = {
  active: "green", published: "green", paid: "green", captured: "green", fulfilled: "green", succeeded: "green", connected: "green", verified: "green", completed: "green", delivered: "green", approved: "green", refunded: "neutral",
  draft: "neutral", archived: "neutral", cancelled: "neutral", closed: "neutral", not_required: "neutral", disconnected: "neutral", expired: "neutral",
  pending: "yellow", awaiting_action: "yellow", unfulfilled: "yellow", partially_fulfilled: "blue", partially_refunded: "orange", requested: "yellow", setup_required: "yellow", open: "blue", authorized: "blue", received: "blue", trialing: "blue", shipped: "blue",
  failed: "red", error: "red", disputed: "red", suspended: "red", rejected: "red", reauth_required: "red", past_due: "red", chargeback: "red", urgent: "red",
};
export function humanize(s: string) {
  return s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return <Badge tone={STATUS_TONES[status] ?? "neutral"}>{label ?? <StatusText status={status} />}</Badge>;
}

export function Alert({ tone = "info", title, children, className }: { tone?: "info" | "success" | "warning" | "error"; title?: ReactNode; children?: ReactNode; className?: string }) {
  const s = { info: "bg-sky-50 text-sky-900 ring-sky-200", success: "bg-emerald-50 text-emerald-900 ring-emerald-200", warning: "bg-amber-50 text-amber-900 ring-amber-200", error: "bg-red-50 text-red-900 ring-red-200" }[tone];
  return (
    <div role={tone === "error" ? "alert" : "status"} className={cx("rounded-lg px-4 py-3 text-sm ring-1 ring-inset", s, className)}>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? "mt-1" : ""}>{children}</div>}
    </div>
  );
}

export function EmptyState({ title, description, action, icon }: { title: ReactNode; description?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">{icon ?? <span aria-hidden className="text-xl">◎</span>}</div>
      <h3 className="text-base font-semibold text-zinc-900">{title}</h3>
      {description && <p className="mt-1 max-w-md text-sm text-zinc-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: "default" | "muted" }) {
  return (
    <Card className="p-5">
      <p className="text-sm font-medium text-zinc-500">{label}</p>
      <p className={cx("mt-1 text-2xl font-semibold tracking-tight", tone === "muted" ? "text-zinc-500" : "text-zinc-900")}>{value}</p>
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </Card>
  );
}

/** Responsive table wrapper: horizontal scroll inside the card, never the page. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx("relative overflow-x-auto", className)}>
      <table className="min-w-full divide-y divide-zinc-200 text-sm">{children}</table>
    </div>
  );
}
export function Th({ className, ...p }: ComponentProps<"th">) {
  return <th scope="col" {...p} className={cx("px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-zinc-500", className)} />;
}
export function Td({ className, ...p }: ComponentProps<"td">) {
  return <td {...p} className={cx("px-4 py-3 align-middle text-zinc-700", className)} />;
}

/** Link-based pagination preserving other query params. */
export function Pagination({ page, perPage, total, basePath, params = {} }: { page: number; perPage: number; total: number; basePath: string; params?: Record<string, string | undefined> }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return <p className="px-4 py-3 text-xs text-zinc-500"><UiText k="ui.results" vars={{ count: total }} /></p>;
  const href = (p: number) => {
    const q = new URLSearchParams(Object.entries({ ...params, page: String(p) }).filter(([, v]) => v !== undefined && v !== "") as [string, string][]);
    return `${basePath}?${q.toString()}`;
  };
  return (
    <UiNav labelKey="ui.pagination" className="flex items-center justify-between gap-3 border-t border-zinc-100 px-4 py-3 text-sm">
      <p className="text-zinc-500">
        <UiText k="ui.pageSummary" vars={{ page, pages, total, count: total }} />
      </p>
      <div className="flex gap-2">
        {page > 1 ? <ButtonLink href={href(page - 1)} variant="secondary" size="sm"><UiText k="common.previous" /></ButtonLink> : <span className={buttonClass("secondary", "sm", "pointer-events-none opacity-40")}><UiText k="common.previous" /></span>}
        {page < pages ? <ButtonLink href={href(page + 1)} variant="secondary" size="sm"><UiText k="common.next" /></ButtonLink> : <span className={buttonClass("secondary", "sm", "pointer-events-none opacity-40")}><UiText k="common.next" /></span>}
      </div>
    </UiNav>
  );
}

/** Link tabs (server-rendered, URL is the state). */
export function Tabs({ tabs, current }: { tabs: { href: string; label: ReactNode; key: string; count?: number }[]; current: string }) {
  return (
    <div className="mb-4 overflow-x-auto border-b border-zinc-200">
      <UiNav labelKey="ui.tabs" className="-mb-px flex gap-6">
        {tabs.map((t) => (
          <Link key={t.key} href={t.href} aria-current={t.key === current ? "page" : undefined} className={cx("whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium", t.key === current ? "border-brand-600 text-brand-700" : "border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-700")}>
            {t.label}
            {t.count !== undefined && <span className="ms-2 rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">{t.count}</span>}
          </Link>
        ))}
      </UiNav>
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cx("animate-pulse rounded-md bg-zinc-200/70", className)} />;
}

export function PageSkeleton() {
  return (
    <div role="status" className="space-y-6">
      <span className="sr-only">
        <UiText k="common.loading" />
      </span>
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

export function DescriptionList({ items }: { items: { label: ReactNode; value: ReactNode }[] }) {
  return (
    <dl className="divide-y divide-zinc-100">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-3 gap-4 py-2.5 text-sm">
          <dt className="text-zinc-500">{it.label}</dt>
          <dd className="col-span-2 text-zinc-900">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Minimal accessible SVG bar chart (no dependencies). */
export function BarChart({ data, format, height = 180, label }: { data: { label: string; value: number }[]; format: (n: number) => string; height?: number; label: string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const w = 100 / Math.max(1, data.length);
  return (
    <figure>
      <svg viewBox={`0 0 100 ${height / 3}`} preserveAspectRatio="none" className="h-44 w-full" role="img" aria-label={label}>
        {data.map((d, i) => {
          const h = (d.value / max) * (height / 3 - 2);
          return (
            <rect key={i} x={i * w + w * 0.15} y={height / 3 - h} width={w * 0.7} height={Math.max(h, d.value > 0 ? 0.6 : 0)} rx={0.6} className="fill-brand-500">
              <title>{`${d.label}: ${format(d.value)}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="mt-1 flex justify-between text-xs text-zinc-500">
        <span>{data[0]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </figcaption>
    </figure>
  );
}

export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-zinc-600">
        <span>{label}</span>
        <span>
          {value} / {max}
        </span>
      </div>
      <div className="h-2 rounded-full bg-zinc-100" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} aria-label={label}>
        <div className={cx("h-2 rounded-full", pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-brand-500")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
